import React, { FunctionComponent, useEffect, useState } from 'react';
import { Box, Button, Link, makeStyles, Paper, Typography } from '@material-ui/core';
import InvoiceIcon from '@material-ui/icons/MonetizationOn';
import PaymentIcon from '@material-ui/icons/Payment';
import { AccountingTransactionDisplayList, License } from '../../../store/modules/application/types';
import TableEmptyState from '../../../components/TableView/TableEmptyState';
//import Loader from '../../../components/Loader';
import { displayCurrencyAmount, getPersonSimpleName } from '../../../utils/utils';
import Modal from '../../../components/Modal';
import NewInvoiceDialog from '../../../components/NewInvoiceDialog';
import { JournalEntry } from '../../../store/modules/accounting/types';
import { FETCH_STATUS } from '../../../store/app.constants';
import ReceivePaymentDialog from '../../../components/ReceivePaymentDialog';
import InvoicesDialog from '../../../components/InvoicesDialog';
import { Person } from '../../../store/modules/person/types';
import InfiniteScrollTableView from '../../../components/InfiniteScrollTableView';
import RefreshIcon from '@material-ui/icons/Refresh';

type AccountingProps = {
    person: Person;
    license?: License;
    transactionList: AccountingTransactionDisplayList;
    getAccounting: Function;
    addJournalEntry: Function;
    loading: boolean;
    updateStatus: string;
    hasMore: boolean;
    limit: number;
    setSkip: Function;
    skip: number;
    errorStatus: boolean;
};

interface IColumn {
    id: 'date|date' | 'description|string' | 'chargeAmount|currency' | 'paymentAmount|currency' | 'balance|currency';
    label: string;
    align?: 'left' | 'right' | 'center';
    field: string;
    noSort?: boolean;
}

const columns: IColumn[] = [
    { id: 'date|date', label: 'Date', align: 'left', field: 'date', noSort: true },
    { id: 'description|string', label: 'Description', align: 'left', field: 'description', noSort: true },
    { id: 'chargeAmount|currency', label: 'Charge', align: 'right', field: 'chargeAmount', noSort: true },
    { id: 'paymentAmount|currency', label: 'Payment', align: 'right', field: 'paymentAmount', noSort: true },
    { id: 'balance|currency', label: 'Balance', align: 'right', field: 'balance', noSort: true },
];

const useStyles = makeStyles((theme) => ({
    toolbar: {
        display: 'flex',
        marginBottom: theme.spacing(3),
    },
    toolbarItem: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
    },
    toolbarSpacer: {
        flexGrow: 1,
    },
    linkButton: {
        textTransform: 'none',
    },
    balanceSummary: {
        fontWeight: 'bold',
        fontSize: 'x-large',
    },
    uploadButton: {
        color: '#0277BD',
        marginRight: '40px',
        fontWeight: 600,
        cursor: 'pointer',
        letterSpacing: '0.3px,',
        display: 'flex',
        alignItems: 'center',
        marginBottom: '25px',
        justifyContent: 'flex-end',
    },
    uploadButtonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    link: {
        color: '#0277BD',
        textAlign: 'center',
        marginLeft: '8px',
        marginRight: '8px',
        '&:hover': {
            cursor: 'pointer',
        },
    },
    searchIconContainer: {
        width: '100%',
        maxHeight: 300,
        color: 'gray',
        paddingTop: '40px',
        paddingBottom: '150px',
    },
    noResultFound: {
        fontSize: '24px',
        color: '#484848',
        lineHeight: '24px',
        fontWeight: 600,
        marginTop: '3rem',
        textAlign: 'center',
    },
}));

const Accounting: FunctionComponent<AccountingProps> = (props) => {
    const [invoicesModal, setInvoicesModal] = useState(false);
    const [newInvoiceModal, setNewInvoiceModal] = useState(false);
    const [receivePaymentModal, setReceivePaymentModal] = useState(false);
    const [updating, setUpdating] = useState(false);
    const {
        person,
        license,
        transactionList,
        getAccounting,
        addJournalEntry,
        //loading,
        updateStatus,
        hasMore,
        limit,
        setSkip,
        skip,
        errorStatus,
    } = props;

    const classes = useStyles();

    // If provided, retrieve license transactions; otherwise, retrieve the person transactions.
    const recordId = license?.id ?? person?.id;

    useEffect(() => {
        if (updateStatus === FETCH_STATUS.LOADING) {
            setUpdating(true);
        } else if (updating) {
            if (updateStatus === FETCH_STATUS.SUCCESS) {
                setUpdating(false);

                // Close dialogs
                setNewInvoiceModal(false);
                setReceivePaymentModal(false);

                // Refresh accounting data
                getAccounting(recordId);
            } else if (updateStatus === FETCH_STATUS.ERROR) {
                setUpdating(false);
            }
        }
    }, [updateStatus, updating, getAccounting, recordId]);

    const onClickRow = (id: number, e: any) => {
        e.stopPropagation();
    };

    const handleNewJournalEntry = (journalEntry: JournalEntry) => {
        journalEntry.lineItems.forEach((lineItem) => {
            lineItem.person = {
                id: person.id.toString(),
                name: getPersonSimpleName(person.name ?? ''),
            };

            if (license) {
                lineItem.license = {
                    id: license.id,
                    licenseNumber: license.licenseNumber,
                    type: license.type,
                };
            }
        });
        addJournalEntry(journalEntry);
        setSkip(0);
    };

    const loadMore = () => {
        if (hasMore) {
            setSkip(skip + 1);
        }
    };

    // TODO: Return loader if loading files. Currently the loader takes up the complete page.
    // This causes issues when uploading a file or loading more files when scrolling as it
    // degrades the user experience. Instead it should be able to differentiate bewteen the
    // scenarios and only display the loader when appropriate.
    // if (loading) {
    //     return <Loader />;
    // }

    if (errorStatus) {
        return (
            <div className="verticalFillContainer">
                <div className={classes.uploadButtonContainer}>
                    <Button
                        startIcon={<RefreshIcon />}
                        className={classes.uploadButton}
                        onClick={() => getAccounting(recordId, { limit })}
                    >
                        RELOAD TRANSACTIONS
                    </Button>
                </div>
                <Paper className="case-entries-container verticalFillContent">
                    {
                        <Box className={classes.searchIconContainer} display="flex" flexDirection="column">
                            <Box className={classes.noResultFound} display="flex" justifyContent="center">
                                Unable to load transactions. Please try{' '}
                                <Link className={classes.link} onClick={() => getAccounting(recordId, { limit })}>
                                    reloading
                                </Link>{' '}
                                in a few moments.
                            </Box>
                        </Box>
                    }
                </Paper>
            </div>
        );
    }

    const balance = transactionList.length > 0 ? transactionList[0].balance : 0;

    return (
        <div className="verticalFillContainer">
            <div className={classes.toolbar}>
                <div className={classes.toolbarItem}>
                    <Typography color="primary" className={classes.balanceSummary}>
                        Balance: {displayCurrencyAmount(balance)}
                    </Typography>
                </div>
                <div className={classes.toolbarItem}>
                    <Button
                        variant="text"
                        color="primary"
                        className={classes.linkButton}
                        onClick={() => setInvoicesModal(true)}
                    >
                        Show Invoices
                    </Button>
                </div>

                <div className={classes.toolbarSpacer} />

                <>
                    <Button
                        color="primary"
                        variant="contained"
                        disableElevation
                        startIcon={<InvoiceIcon />}
                        className={classes.toolbarItem}
                        onClick={() => setNewInvoiceModal(true)}
                    >
                        New Invoice
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        disableElevation
                        startIcon={<PaymentIcon />}
                        className={classes.toolbarItem}
                        onClick={() => setReceivePaymentModal(true)}
                    >
                        Receive Payment
                    </Button>
                </>
            </div>
            <Paper className="case-entries-container verticalFillContent">
                {transactionList.length > 0 ? (
                    <InfiniteScrollTableView
                        columns={columns}
                        data={transactionList}
                        count={transactionList.length}
                        sortBy={'date'}
                        onClickRow={onClickRow}
                        hasMore={hasMore}
                        loadMore={loadMore}
                    />
                ) : (
                    <TableEmptyState
                        title="No accounting records"
                        message="Accounting records will be displayed here"
                    ></TableEmptyState>
                )}
            </Paper>
            <Modal open={invoicesModal} maxWidth={'lg'} fullWidth={true} minHeight={'80vh'} padding={0}>
                <InvoicesDialog person={person} license={license} onClose={() => setInvoicesModal(false)} />
            </Modal>
            <Modal open={newInvoiceModal} maxWidth={'lg'} fullWidth={true} minHeight={'10vh'}>
                <NewInvoiceDialog
                    person={person}
                    license={license}
                    onClose={() => setNewInvoiceModal(false)}
                    onSave={handleNewJournalEntry}
                    updating={updating}
                />
            </Modal>
            <Modal open={receivePaymentModal} maxWidth={'md'} fullWidth={true} minHeight={'10vh'}>
                <ReceivePaymentDialog
                    personId={person.id}
                    licenseId={license?.id}
                    onClose={() => setReceivePaymentModal(false)}
                    onSave={handleNewJournalEntry}
                    updating={updating}
                />
            </Modal>
        </div>
    );
};

export default Accounting;
